// Shared layout used by both Incoming Email and Incoming Text ports.
// Approved HTML: Incoming-Email-Consistency-Review-v5.html and
// Incoming-Text-Consistency-Review-v5.html share the same structure;
// the mock data differentiates channel, destination, subject etc.
//
// W31 (2026-10-20): on a real session the shell renders the HubSpot
// profile (breadcrumb, identity row, recipient) and an honest
// "No incoming email / text for this contact" in place of the sample
// thread. No inbound Zoho / Kixie thread read exists yet (W37), so the
// prepared reply starts empty and Send Reply stays visible but inert
// behind the live-write gate. The SARAH / Duke / Jamie fixture renders
// ONLY in preview or dev-mock mode.
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../../_shared/DataBoundary.js';
import { IdentityRow } from '../../_shared/IdentityRow.js';
import { CustomerTabs } from '../../_shared/CustomerTabs.js';
import { Icon } from '../../_shared/IconSprite.js';
import { ActionFooter } from '../../_shared/ActionFooter.js';
import { RepriorityPill } from '../../_shared/PriorityFocus.js';
import { QueueUpNext } from '../../_shared/UpNext.js';
import { SARAH, HORSE_PORTRAIT_PLACEHOLDER } from '../../../data/mockData.js';
import { getReviewReturnTarget } from '../../_shared/ReviewState.js';
import { useScreenIdentity } from '../../_w2a_lower/identity.js';
import { WriteButton } from '../../_w2a_lower/liveWrite.js';

export interface IncomingShellMock {
  heroEyebrow: string;
  heroTitle: string;
  heroContext: string;
  purpose: string;
  reason: string;
  recipient: string;
  destination: string;
  subject?: string;
  sender: string;
  sentAt: string;
  lastMessage: string;
  draft: string;
  draftHint: string;
  channelLabel: string;
  threadLabel: string;
}

export type IncomingShellVariant = 'email' | 'text' | 'livechat';

export interface IncomingShellProps {
  variant: IncomingShellVariant;
  mock: IncomingShellMock;
  blockedGate: string;
  blockedTitle: string;
  blockedReason: string;
  testId: string;
  stateOverride?: BoundaryState;
}

function iconForVariant(v: IncomingShellVariant): string {
  // Live chat and text both use the message icon; email keeps mail.
  return v === 'email' ? 'mail' : 'message';
}

function noun(v: IncomingShellVariant): string {
  if (v === 'email') return 'Email';
  if (v === 'livechat') return 'Live chat';
  return 'Text';
}

function nounLower(v: IncomingShellVariant): string {
  if (v === 'email') return 'email';
  if (v === 'livechat') return 'live chat';
  return 'text';
}

function actionNameFor(v: IncomingShellVariant): string {
  if (v === 'email') return 'zoho_reply_to_customer';
  if (v === 'livechat') return 'hubspot_conversations_reply';
  return 'kixie_webhook_inbound';
}

function errorCodeFor(v: IncomingShellVariant): string {
  if (v === 'email') return 'INCOMING_EMAIL_UNAVAILABLE';
  if (v === 'livechat') return 'INCOMING_LIVE_CHAT_UNAVAILABLE';
  return 'INCOMING_TEXT_UNAVAILABLE';
}

function errorTitleFor(v: IncomingShellVariant): string {
  if (v === 'email') return 'Email thread could not load';
  if (v === 'livechat') return 'Live chat thread could not load';
  return 'Text thread could not load';
}

function heroTitleFor(v: IncomingShellVariant): string {
  if (v === 'email') return 'Incoming Email';
  if (v === 'livechat') return 'Incoming Live Chat';
  return 'Incoming Text';
}

/** data-screen key of the companion route; scopes the screen's stylesheet (W40.2). */
export function screenKeyFor(v: IncomingShellVariant): string {
  if (v === 'email') return 'incoming-email';
  if (v === 'livechat') return 'incoming-live-chat';
  return 'incoming-text';
}

function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Everything the shell renders, resolved once from fixture or real data. */
interface IncomingShellView extends IncomingShellMock {
  /** False on a real session: no inbound thread source exists yet. */
  hasThread: boolean;
  emptyTitle: string;
  emptyBody: string;
}

export function IncomingShell({
  variant,
  mock,
  blockedGate,
  blockedTitle,
  blockedReason,
  testId,
  stateOverride,
}: IncomingShellProps): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const customerId = params.customerId ?? SARAH.id;
  const identity = useScreenIdentity(customerId);
  const useFixture = identity.isPreview;
  const customer = identity.customer;
  const firstName = identity.firstName;
  const possessive = identity.possessive;
  const paused = identity.liveWritesPaused;
  const lower = nounLower(variant);

  const view: IncomingShellView = useFixture
    ? { ...mock, hasThread: true, emptyTitle: '', emptyBody: '' }
    : {
        heroEyebrow: 'CUSTOMER CONVERSATION',
        heroTitle: heroTitleFor(variant),
        heroContext: `No incoming ${lower} on file for ${customer.fullName}`,
        purpose: `No incoming ${lower} for this contact`,
        reason: `Inbound ${lower} threads are not connected to the workspace yet.`,
        recipient: customer.fullName,
        destination:
          variant === 'email'
            ? customer.email || 'No email on file'
            : customer.phone || 'No phone on file',
        sender: '',
        sentAt: '',
        lastMessage: '',
        draft: '',
        draftHint: `${noun(variant)} to ${
          variant === 'email'
            ? customer.email || 'no email on file'
            : customer.phone || 'no phone on file'
        } · Send channel not connected yet`,
        channelLabel: `${noun(variant)} message`,
        threadLabel: `LATEST ${noun(variant).toUpperCase()} MESSAGE`,
        hasThread: false,
        emptyTitle: `No incoming ${lower} for this contact`,
        emptyBody: `When ${firstName} writes in, the ${lower} thread opens here.`,
      };

  const [draft, setDraft] = useState(view.draft);
  useEffect(() => {
    setDraft(view.draft);
  }, [view.draft]);
  const [sent, setSent] = useState(false);
  const [retained, setRetained] = useState(false);
  const [threadExpanded, setThreadExpanded] = useState(false);
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const state = resolveState(kind, stateOverride, blockedTitle, blockedReason, blockedGate, variant);
  const finishDisabled = !(sent || retained);
  const returnTarget = getReviewReturnTarget(location.state, customerId);
  const profile = identity.profile;

  return (
    <WorkspaceChrome screen={screenKeyFor(variant)} rail="my-work">
      {/* W40: the approved companions have no breadcrumb row (MEASURED, 0
          entries); the row stays only as the carrier of the preview label. */}
      {useFixture ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>{customer.fullName}</strong>{' '}
            <span className="slash">/</span> {view.heroTitle}
          </div>
          <span className="preview">Design preview · Sample conversation · No live send</span>
        </div>
      ) : null}
      <section className="cc-hero" aria-label="Current task">
        <div className="sf-hero-channel">
          <Icon id={iconForVariant(variant)} />
          <div>
            <span className="eyebrow">{view.heroEyebrow}</span>
            <h1>{view.heroTitle}</h1>
            <span>{view.heroContext}</span>
          </div>
        </div>
        <div className="sf-hero-task-copy">
          <h2>{view.purpose}</h2>
          <p>{view.reason}</p>
        </div>
        {/* W40: approved pill, informational, on every session. */}
        <div className="shared-repriority-heading">
          <RepriorityPill className="shared-repriority" />
        </div>
      </section>
      <IdentityRow
        preferenceNote="The customer’s request guides this conversation."
        customer={customer}
        badgeLabel={view.heroTitle}
        {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
      />
      <CustomerTabs customerId={customerId} active="current" />
      <DataBoundary state={state} testId={testId}>
        <section className="sf-task-dock" aria-label="Current conversation">
          <div className="sf-task-identity">
            <div className="sf-task-person">
              <span className="sf-task-avatar" aria-hidden="true">
                {customer.initials}
              </span>
              <div>
                <h3>{view.recipient}</h3>
                <p>{view.destination}</p>
              </div>
            </div>
            <span className="sf-task-badge">{view.heroTitle}</span>
            <div className="sf-task-dock-cue">
              <b>{`${noun(variant)} reply & notes`}</b>
              <span>Notes stay with this conversation</span>
            </div>
          </div>
        </section>
        <div id="sf-task-workspace">
          <div className="sf-grid">
            <section className="card conversation" aria-label="Current follow-up">
              <div className="card-head">
                <div className="row">
                  <span className="head-icon">
                    <Icon id={iconForVariant(variant)} />
                  </span>
                  <h3>Message to {firstName}</h3>
                </div>
                <span className="sf-channel">{view.channelLabel}</span>
              </div>
              {view.subject ? (
                <p
                  className="sf-subject"
                  style={{ padding: '12px 20px 0', fontSize: 12, color: '#4b6590' }}
                >
                  <strong>Subject:</strong> {view.subject}
                </p>
              ) : null}
              <div className="thread">
                {view.hasThread ? (
                  <>
                    <div className="sf-thread-toolbar">
                      <span className="eyebrow">{view.threadLabel}</span>
                      <button
                        type="button"
                        className="sf-thread-toggle"
                        aria-expanded={threadExpanded}
                        onClick={() => setThreadExpanded(!threadExpanded)}
                      >
                        <span>{threadExpanded ? 'Collapse thread history' : 'Expand thread history'}</span>
                        <Icon id="arrow" />
                      </button>
                    </div>
                    <div
                      className="sf-thread-window"
                      role="region"
                      tabIndex={0}
                      aria-label="Earlier messages in this thread"
                      hidden={!threadExpanded}
                    >
                      <p className="muted" style={{ padding: '12px 18px' }}>
                        Earlier messages in this thread appear here when the shared conversation
                        adapter is approved.
                      </p>
                    </div>
                    <div className="message-meta sf-last-message-meta">
                      <strong>{view.sender}</strong>
                      <span>{view.sentAt}</span>
                    </div>
                    <p className="bubble sf-latest-message">{view.lastMessage}</p>
                  </>
                ) : (
                  <div
                    role="status"
                    data-testid={`${testId}-empty`}
                    className="muted"
                    style={{ padding: '12px 18px' }}
                  >
                    <strong style={{ display: 'block', marginBottom: 4 }}>{view.emptyTitle}</strong>
                    <span>{view.emptyBody}</span>
                  </div>
                )}
              </div>
              <div className="draft">
                <div className="draft-top">
                  <strong>Prepared reply</strong>
                  <span>Edit before sending</span>
                </div>
                <textarea
                  aria-label="Prepared reply"
                  value={draft}
                  onChange={event => setDraft(event.target.value)}
                />
                <div className="draft-bottom">
                  <WriteButton
                    paused={paused}
                    className="primary"
                    disabled={!draft.trim() || sent}
                    onClick={() => setSent(true)}
                    actionLabel="Send Reply"
                  >
                    <Icon id="icon-send" />
                    {sent ? 'Reply sent' : 'Send Reply'}
                  </WriteButton>
                  <span>{view.draftHint}</span>
                </div>
              </div>
              <div className="sf-retain-row">
                <span>Need information or a later conversation?</span>
                <button type="button" onClick={() => setRetained(true)} disabled={retained}>
                  {retained ? 'Retained' : 'Still needs attention'}
                </button>
              </div>
            </section>
            <aside className="right-stack">
              <section className="card">
                <div className="context">
                  <div className="eyebrow">CUSTOMER CONTEXT</div>
                  {useFixture ? (
                    <>
                      <div className="row">
                        <img
                          className="sf-horse"
                          alt="Illustrative approved horse portrait for Duke"
                          src={HORSE_PORTRAIT_PLACEHOLDER}
                        />
                        <div>
                          <h3>Duke</h3>
                          <p className="muted">Quarter Horse · Sarah’s horse</p>
                        </div>
                      </div>
                      <dl>
                        <dt>Product</dt>
                        <dd>Gut X</dd>
                        <dt>Subscription</dt>
                        <dd>Paused by customer</dd>
                        <dt>Customer need</dt>
                        <dd>{view.purpose}</dd>
                      </dl>
                    </>
                  ) : (
                    <dl data-testid={`${testId}-context`}>
                      <dt>Lifecycle stage</dt>
                      <dd>{profile?.lifecycleStage ? titleCase(profile.lifecycleStage) : 'Not set'}</dd>
                      <dt>Last VanillaSoft call</dt>
                      <dd>{profile?.lastCallDate ?? 'None logged'}</dd>
                      <dt>Customer need</dt>
                      <dd>{view.purpose}</dd>
                    </dl>
                  )}
                </div>
              </section>
              <section className="card sf-guidance">
                <span className="eyebrow">CONVERSATION GUIDE</span>
                <h3>Start with {possessive} request.</h3>
                <p>
                  Offer useful help. Confirm {possessive} current need before advancing to the next
                  order.
                </p>
              </section>
              <section className="card">
                <div className="context">
                  <div className="eyebrow">CURRENT TASK</div>
                  <dl>
                    <dt>Purpose</dt>
                    <dd>{view.purpose}</dd>
                    <dt>Channel</dt>
                    <dd>{noun(variant)}</dd>
                    <dt>Recipient</dt>
                    <dd>{customer.fullName}</dd>
                    <dt>Status</dt>
                    <dd>
                      {sent
                        ? 'Reply sent'
                        : retained
                          ? 'Retained'
                          : view.hasThread
                            ? 'Ready for reply'
                            : 'No message'}
                    </dd>
                  </dl>
                </div>
              </section>
            </aside>
          </div>
        </div>
        {useFixture ? (
          <section className="next" aria-label="Next work preview">
            <span className="eyebrow">UP NEXT</span>
            <div className="avatar">TB</div>
            <div>
              <strong>Taylor Brooks</strong>
              <p>Customer {lower} reply</p>
            </div>
            <div className="reason">
              <strong>Waiting reply</strong>
              <p>After this task is finished</p>
            </div>
            <Icon id="arrow" />
          </section>
        ) : <QueueUpNext customerId={customerId} />}
      </DataBoundary>
      <ActionFooter
        disabled={finishDisabled}
        noteWhenDisabled={'Send a reply or choose "Still needs attention".'}
        noteWhenEnabled="Reply recorded · Ready for next customer"
        summaryStrong={`${view.heroTitle} · ${customer.fullName}`}
        summaryBody="AI selects the next eligible work after this task is saved."
        onFinish={() => navigate(returnTarget.path, { state: returnTarget.state })}
      />
      {useFixture && !stateOverride ? <StatePreview kind={kind} onChange={setKind} /> : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
  blockedTitle: string,
  blockedReason: string,
  blockedGate: string,
  variant: IncomingShellVariant,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return {
        kind: 'loading',
        label: `Loading incoming ${nounLower(variant)}…`,
      };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No incoming message',
        body: 'When a customer replies the conversation opens here.',
      };
    case 'error':
      return {
        kind: 'error',
        title: errorTitleFor(variant),
        detail: 'The channel adapter did not respond.',
        errorCode: errorCodeFor(variant),
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'sf-customer-context', reason: 'horse_timeline_slow' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: blockedTitle,
        reason: blockedReason,
        gateName: blockedGate,
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: `Sending ${nounLower(variant)} reply…`,
        actionName: actionNameFor(variant),
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}
